import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { ITaskV2AssignedUser } from '@fi-app/task-v2/fi-task-v2';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { ILightWeightObject } from '@fi-shared/models';
import { TextTruncationService, ITruncatedList } from '../../../../shared/fi-shared-services/text-truncation.service';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { BusinessUnitService } from '@fi-app/business-unit/business-unit.service';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { getSortedRoles, getSortedUnits } from './assignee-roles-units-sort';
import { ObjectTypes } from '@fi-shared/types';
import { getObjectTypeNameTextId } from '@fi-shared/utils';

@Component({
    selector: 'fi-task-v2-assignees-list',
    templateUrl: './fi-task-v2-assignees-list.component.html',
    styleUrls: ['./fi-task-v2-assignees-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-task-v2-assignees-list' },
    standalone: false,
})
export class FiTaskV2AssigneesListComponent {
    @Input({ required: true }) assignedUsers: ITaskV2AssignedUser[];
    @Input() taskRoles: ILightWeightObject[] = [];
    @Input() taskUnits: ILightWeightObject[] = [];

    @ViewChild('rolesContainer') rolesContainer: ElementRef;
    @ViewChild('unitsContainer') unitsContainer: ElementRef;

    protected readonly FiProfileSizes = FiProfileSizes;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly ObjectTypes = ObjectTypes;
    protected rolesLabelWidth = 0;
    protected unitsLabelWidth = 0;
    protected truncatedRoles: Map<number, ITruncatedList<ILightWeightObject>> = new Map();
    protected truncatedUnits: Map<number, ITruncatedList<ILightWeightObject>> = new Map();

    constructor(
        private truncationService: TextTruncationService,
        private appTextService: AppTextService,
        private businessUnitService: BusinessUnitService,
    ) {}

    protected onRolesLabelWidthChange(width: number): void {
        this.rolesLabelWidth = width;
        this.updateTruncatedRoles();
    }

    protected onUnitsLabelWidthChange(width: number): void {
        this.unitsLabelWidth = width;
        this.updateTruncatedUnits();
    }

    private updateTruncatedRoles(): void {
        if (!this.rolesContainer) {
            return;
        }

        this.assignedUsers.forEach((assignee) => {
            const truncated = this.truncationService.calculateTruncatedList(
                this.rolesContainer,
                getSortedRoles(assignee, this.taskRoles),
                this.rolesLabelWidth,
                this.appTextService.get('common.roles'),
            );
            this.truncatedRoles.set(assignee.Id, truncated);
        });
    }

    private updateTruncatedUnits(): void {
        if (!this.unitsContainer) {
            return;
        }

        this.assignedUsers.forEach((assignee) => {
            const truncated = this.truncationService.calculateTruncatedList(
                this.unitsContainer,
                getSortedUnits(assignee, this.taskUnits),
                this.unitsLabelWidth,
                this.appTextService.get('common.units'),
            );
            this.truncatedUnits.set(assignee.Id, truncated);
        });
    }

    protected getObjectTypeTextId(objectType: ObjectTypes, count: number): string {
        return this.appTextService.get(getObjectTypeNameTextId(objectType, count !== 1)).toLowerCase();
    }

    protected getAssigneeUnitsData(assignee: ITaskV2AssignedUser): ITruncatedList<ILightWeightObject> {
        return this.truncatedUnits.get(assignee.Id);
    }

    protected getAssigneeRolesData(assignee: ITaskV2AssignedUser): ITruncatedList<ILightWeightObject> {
        return this.truncatedRoles.get(assignee.Id);
    }

    protected openUnitViewPopup(unitId: number): void {
        this.businessUnitService.openBusinessUnitViewDialog(unitId);
    }
}
